import React, { useMemo, memo } from 'react';
import { resolveFileUrlSync } from '../../utils/resolveFileUrl';

// Deterministic color palette
const COLOR_PALETTE = [
  '#3d8ef0', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#a855f7', '#f43f5e', '#0ea5e9', '#22c55e',
];

const getAvatarColor = (seed) => {
  if (!seed) return COLOR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLOR_PALETTE[Math.abs(hash) % COLOR_PALETTE.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

// Named size shortcuts
const SIZE_CLASSES = {
  xs:  { outer: 'w-6 h-6',   font: 'text-[9px]',  dot: 'w-1.5 h-1.5' },
  sm:  { outer: 'w-8 h-8',   font: 'text-[11px]', dot: 'w-2 h-2' },
  md:  { outer: 'w-10 h-10', font: 'text-sm',      dot: 'w-2.5 h-2.5' },
  lg:  { outer: 'w-12 h-12', font: 'text-base',    dot: 'w-3 h-3' },
  xl:  { outer: 'w-16 h-16', font: 'text-xl',      dot: 'w-3.5 h-3.5' },
  '2xl': { outer: 'w-20 h-20', font: 'text-2xl',   dot: 'w-4 h-4' },
};

/**
 * Unified avatar component used across the entire app.
 *
 * Props:
 *   userId    — used for deterministic color (string)
 *   avatarUrl — URL path or full URL; base64 accepted for legacy compat
 *   name      — display name for initials fallback
 *   size      — 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number (px)
 *   online    — boolean | undefined (undefined = no dot)
 *   onClick   — click handler
 *   className — extra classes on the wrapper
 *   ring      — show ring border
 */
const UserAvatar = memo(({
  userId,
  avatarUrl,
  name,
  size = 'md',
  online,
  onClick,
  className = '',
  ring = false,
}) => {
  const isNumericSize = typeof size === 'number';
  const sizeInfo = isNumericSize ? null : (SIZE_CLASSES[size] ?? SIZE_CLASSES.md);
  const outerStyle = isNumericSize ? { width: size, height: size } : {};
  const fontStyle = isNumericSize ? { fontSize: Math.round(size * 0.35) } : {};

  const resolvedUrl = useMemo(() => {
    if (!avatarUrl) return null;
    // Already absolute or data URL
    if (avatarUrl.startsWith('data:') || avatarUrl.startsWith('http') || avatarUrl.startsWith('blob:')) return avatarUrl;
    return resolveFileUrlSync(avatarUrl);
  }, [avatarUrl]);

  const color = useMemo(() => getAvatarColor(userId || name), [userId, name]);
  const initials = useMemo(() => getInitials(name), [name]);

  const outerCls = [
    'relative flex-shrink-0 rounded-full',
    isNumericSize ? '' : sizeInfo.outer,
    ring ? 'ring-2 ring-white/[0.10]' : '',
    onClick ? 'cursor-pointer' : '',
    className,
  ].filter(Boolean).join(' ');

  const dotSizeCls = isNumericSize
    ? undefined
    : (sizeInfo?.dot ?? 'w-2.5 h-2.5');
  const dotStyle = isNumericSize
    ? { width: Math.max(6, Math.round(size * 0.22)), height: Math.max(6, Math.round(size * 0.22)) }
    : {};

  return (
    <div className={outerCls} style={outerStyle} onClick={onClick} role={onClick ? 'button' : undefined}>
      {resolvedUrl ? (
        <img
          src={resolvedUrl}
          alt={name || ''}
          className="w-full h-full rounded-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextSibling?.style && (e.currentTarget.nextSibling.style.display = 'flex');
          }}
        />
      ) : null}
      {/* Initials fallback — also shown when img errors */}
      <div
        className={`${resolvedUrl ? 'hidden' : 'flex'} w-full h-full rounded-full items-center justify-center font-semibold text-white select-none`}
        style={{ backgroundColor: color, ...fontStyle }}
      >
        {initials}
      </div>

      {/* Online indicator */}
      {online !== undefined && (
        <span
          className={`absolute bottom-0 right-0 rounded-full border-2 border-[#0d1424] ${online ? 'bg-green-400' : 'bg-gray-500'} ${dotSizeCls ?? ''}`}
          style={dotStyle}
        />
      )}
    </div>
  );
});

UserAvatar.displayName = 'UserAvatar';
export default UserAvatar;
export { getAvatarColor, getInitials };
